import { createMongoAbility, MongoAbility } from '@casl/ability'

export type CaslRule = { action: string; subject: string }
export type AppAbility = MongoAbility

export function buildAbility(caslRules: CaslRule[]): AppAbility {
  return createMongoAbility(caslRules)
}

/**
 * Normalize backend subject strings so CASL checks stay consistent (e.g. "dashboard" vs "Dashboard").
 *
 * Multi-word subjects must survive intact: naively lowercasing everything after the first
 * character turns "ProviderService" into "Providerservice", which then never matches
 * ability.can('manage', 'ProviderService'). Single-word subjects (Dashboard, Order, Provider,
 * Finance, System) behave exactly as before.
 */
export function normalizeCaslSubject(subject: string): string {
  const s = subject.trim()
  if (!s) return s
  if (s.toLowerCase() === 'all') return 'all'

  // "provider_service" / "provider-service" -> "ProviderService"
  if (/[_-]/.test(s)) {
    return s
      .split(/[_-]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join('')
  }

  // Already has an internal capital -> preserve it ("providerService" -> "ProviderService")
  if (/[A-Z]/.test(s.slice(1))) return s.charAt(0).toUpperCase() + s.slice(1)

  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

/** True if the built ability allows opening the overview route / sidebar Dashboard link. */
export function canReadDashboard(ability: AppAbility): boolean {
  if (ability.can('read', 'Dashboard')) return true
  if (ability.can('manage', 'Dashboard')) return true
  if (ability.can('read', 'all') || ability.can('manage', 'all')) return true
  return false
}

/**
 * Subject for per-provider service authorization (corporate manager / ops / staff).
 * Backend emits it from ProfileCompany.is_manager, ProfileOperation, or staff.
 */
export const PROVIDER_SERVICE_SUBJECT = 'ProviderService'

/** True if the built ability allows toggling which services a provider may offer. */
export function canManageProviderServices(ability: AppAbility): boolean {
  return ability.can('manage', PROVIDER_SERVICE_SUBJECT)
}

/**
 * Backend may still send read Dashboard for dispatcher/accountant; we align UI with intended roles.
 * Admin (group or manage:all) is never stripped.
 */
export function stripDashboardRulesForRestrictedGroups(
  rules: CaslRule[],
  groups: string[]
): CaslRule[] {
  const normalized = groups.map((g) => g.toLowerCase().trim().replace(/\s+/g, '_'))
  if (normalized.includes('admin')) return rules

  const restrict = normalized.some((g) => g === 'dispatcher' || g === 'accountant')
  if (!restrict) return rules

  return rules.filter((r) => {
    const subj = normalizeCaslSubject(r.subject)
    const act = typeof r.action === 'string' ? r.action.toLowerCase() : ''
    if (subj === 'all') return true
    if (subj === 'Dashboard' && (act === 'read' || act === 'manage')) return false
    return true
  })
}

/**
 * Frontend fallback: maps group names to CASL rules so the UI works
 * even when the backend hasn't populated caslRules on the profile yet.
 * Once the backend returns real caslRules these are ignored.
 */
const GROUP_RULES: Record<string, CaslRule[]> = {
  admin: [
    { action: 'manage', subject: 'all' },
  ],
  dispatcher: [
    { action: 'read', subject: 'Order' },
    { action: 'manage', subject: 'Order' },
  ],
  accountant: [
    { action: 'read', subject: 'Finance' },
  ],
  human_resources: [
    { action: 'read', subject: 'Dashboard' },
    { action: 'read', subject: 'Provider' },
  ],
}

/**
 * Derive CASL rules from group name(s).
 * Handles variations like "human resources", "Human_Resources", "HR", etc.
 */
export function rulesForGroups(groups: string[]): CaslRule[] {
  const rules: CaslRule[] = []
  const seen = new Set<string>()

  for (const group of groups) {
    const key = group.toLowerCase().trim().replace(/\s+/g, '_')
    const matched = GROUP_RULES[key]
    if (matched) {
      for (const rule of matched) {
        const id = `${rule.action}:${rule.subject}`
        if (!seen.has(id)) {
          seen.add(id)
          rules.push(rule)
        }
      }
    }
  }

  return rules
}

/** Union two rule lists, deduping by `${action}:${subject}` (first occurrence wins). */
export function mergeRules(a: CaslRule[], b: CaslRule[]): CaslRule[] {
  const merged: CaslRule[] = []
  const seen = new Set<string>()
  for (const rule of [...a, ...b]) {
    const id = `${rule.action}:${rule.subject}`
    if (!seen.has(id)) {
      seen.add(id)
      merged.push(rule)
    }
  }
  return merged
}
